/**
 * Checks if a member is a facility admin based on their role
 * @param {Object} member - The member to check
 * @returns {boolean} - True if the member is a facility admin, false otherwise
 */
export const isFacilityAdmin = (member) => {
  if (!member || member.type !== 'person' || !member.role || typeof member.role !== 'string') {
    return false;
  }
  return member.role.toLowerCase().includes('facility admin');
};

/**
 * Checks if a member is a learner based on their role
 * @param {Object} member - The member to check
 * @returns {boolean} - True if the member is a learner, false otherwise
 */
export const isLearner = (member) => {
  if (!member || member.type !== 'person' || !member.role || typeof member.role !== 'string') {
    return false;
  }
  return member.role.toLowerCase().includes('learner');
};

/**
 * Gets all learners from the selected members array
 * @param {Array} selectedMembers - Array of selected members
 * @returns {Array} - Array of learner members
 */
export const getLearners = (selectedMembers) => {
  return selectedMembers.filter(isLearner);
};

/**
 * Gets all facility admins from the selected members array
 * @param {Array} selectedMembers - Array of selected members
 * @returns {Array} - Array of facility admin members
 */
export const getFacilityAdmins = (selectedMembers) => {
  return selectedMembers.filter(isFacilityAdmin);
};

/**
 * Validates that exactly one facility admin and at least one learner are selected
 * @param {Object} selectedAdmin - The selected facility admin (single object or null)
 * @param {Array} selectedLearners - Array of selected learners
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateAdminLearnerSelection = (selectedAdmin, selectedLearners) => {
  if (!selectedAdmin) {
    return {
      isValid: false,
      error: 'Please select a facility admin',
      toastMessage: 'You must select a facility admin to create a conversation',
    };
  }

  if (!selectedLearners || selectedLearners.length === 0) {
    return {
      isValid: false,
      error: 'Please select at least one learner',
      toastMessage: 'You must select at least one learner to create a conversation',
    };
  }

  return {
    isValid: true,
    error: '',
  };
};

/**
 * Checks if the selection is valid for creating a conversation
 * (exactly one admin and at least one learner)
 * @param {Object} selectedAdmin - The selected facility admin
 * @param {Array} selectedLearners - Array of selected learners
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidSelectionForConversation = (selectedAdmin, selectedLearners) => {
  const validation = validateAdminLearnerSelection(selectedAdmin, selectedLearners);
  return validation.isValid;
};
